import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";

interface AgentListState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  lastMeta: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };

  documents: any[];
}

const state: AgentListState = {
  loading: false,
  loaded: false,
  fatalError: false,

  lastMeta: {
    page: 0,
    pageSize: 0,
    totalCount: 0,
    totalPages: 0
  },

  documents: []
};

export const ITEMS_LIST = "GET_AGENT_LIST";

export const actions: ActionTree<AgentListState, RootState> = {
  loadData({ commit }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      const columns: any = {
        id: "agent.id",
        name: "agent.name",
        city_id: "city.id",
        city_name: "city.name",
        manager_id: "empl.id",
        manager_name: "empl.name",
        archived_at: "agent.archived_at"
      };

      const filter: any[] = [];

      if (query.name) {
        filter.push({ $ilike: ["agent.name", { $: `%${query.name}%` }] });
      }

      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action({
            columns: columns,
            from: "s:agents",
            from_as: "agent",
            joins: [
              {
                join: "s:employees",
                type: "left",
                as: "empl",
                on: {
                  $eq: ["agent.manager_id", "empl.id"]
                }
              },
              {
                join: "s:cities",
                type: "left",
                as: "city",
                on: {
                  $eq: ["agent.city_id", "city.id"]
                }
              }
            ],
            page_size: 100,
            ...{ page: parseInt(query.page) || 1 },
            ...{
              order: [
                { $desc: ["agent.archived_at"] },
                query.sortDirection == "desc"
                  ? { $desc: [columns[query.sortField || "name"]] }
                  : { $asc: [columns[query.sortField || "name"]] }
              ]
            },
            ...(filter.length > 1
              ? {
                  where: {
                    $and: filter
                  }
                }
              : {
                  where: filter[0]
                })
          })
      ]).then((result: ServerResult) => {
        commit("SET_ITEMS_LIST", result.getOperation(ITEMS_LIST).getContent());
        commit("LOADED_SUCCESS");

        resolve();
      }).catch((error: any) => {
        console.error("[Agents.AgentList.loadData] ERROR", { query, error });
        commit("LOADED_ERROR");
        reject(error);
      });
    });
  },

  toArchive({ commit }, agent_id) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm.$q("AGENT_TO_ARCHIVE").update(agent_id, {
          archived_at: moment().format("YYYY-MM-DDTHH:mm:ss")
        })
      ])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("AGENT_TO_ARCHIVE");

          if (operationResult.hasError()) {
            reject();
          } else {
            resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
          console.error("errror", error);
          reject(error);
        });
    });
  },

  toUnarchive({ commit }, agent_id) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm.$q("AGENT_TO_ARCHIVE").update(agent_id, {
          archived_at: null
        })
      ])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("AGENT_TO_ARCHIVE");

          if (operationResult.hasError()) {
            reject();
          } else {
            resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
          console.error("errror", error);
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<AgentListState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_ITEMS_LIST(state, data) {
    state.documents = data.documents;

    state.lastMeta = {
      page: data.meta.page,
      pageSize: data.meta.page_size,
      totalCount: data.meta.total_count,
      totalPages: data.meta.total_pages
    };
  }
};

const getters = {};

export const STORE_KEY = "$_agents_list";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
